import React, { useMemo } from "react";
import {
  Box,
  Button,
  Divider,
  Grid,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import { StringListEditor, EditorSection, EditorFormNav, EditorListItem, EditorListHeader } from "./institutional/InstitutionalEditorParts";
import EmojiIconPicker from "./shared/EmojiIconPicker";

const emptyQuickLink = () => ({ title: "", href: "", description: "" });
const emptyValueProp = () => ({ icon: "", title: "", body: "" });
const emptySocialLink = () => ({ label: "", href: "" });

const SEGMENT_COPY_KEYS = [
  { key: "geral", label: "Geral" },
  { key: "idoso_gratuidade", label: "Idoso / gratuidade" },
  { key: "estudante", label: "Estudante" },
  { key: "pcd", label: "PcD" },
  { key: "frequente", label: "Passageiro frequente" },
];

export const emptySiteHome = () => ({
  format: "site_home.v1",
  meta: { title: "", description: "", openGraphDescription: "" },
  search: {
    formTitle: "",
    formSubtitle: "",
    origemPlaceholder: "",
    destinoPlaceholder: "",
    quickLinksTitle: "",
    ctaLabel: "",
    ctaSecondaryLabel: "",
    oneWayLabel: "",
    roundTripLabel: "",
    dateIdaLabel: "",
    dateVoltaLabel: "",
    dateIdaFullLabel: "",
    dateVoltaFullLabel: "",
    swapAriaLabel: "",
    origemFieldLabel: "",
    destinoFieldLabel: "",
    origemListLabel: "",
    destinoListLabel: "",
    loadingLabel: "",
    chooseOriginFirstLabel: "",
    searchAriaLabel: "",
    tripTypeAriaLabel: "",
  },
  eyebrow: "",
  heroBadge: "",
  heroTitle: "",
  heroSubtitle: "",
  promoBanner: { line: "", code: "", ctaLabel: "", ctaHref: "" },
  valueProps: [],
  featuredTrips: {
    title: "",
    subtitle: "",
    originLabel: "",
    cardCtaLabel: "",
    fromPriceLabel: "",
  },
  olga: {
    kicker: "",
    title: "",
    body: "",
    bullets: [],
    ctaPrefill: "",
    ctaLabel: "",
    phoneHint: "",
  },
  support: {
    title: "",
    subtitle: "",
    sacLabel: "",
    sacHref: "",
    pcdLabel: "",
    pcdHref: "",
    formLabel: "",
    formHref: "",
    olgaPrefill: "",
    olgaChildren: "",
    accessibilityLabel: "",
    accessibilityHref: "",
  },
  urgentCta: {
    title: "",
    subtitle: "",
    ctaLabel: "",
    ctaHref: "",
  },
  fleet: {
    eyebrow: "",
    title: "",
    body: "",
    ctaLabel: "",
    ctaHref: "",
  },
  seoBlock: { title: "", html: "" },
  paymentCard: { title: "", html: "" },
  segment: { title: "", copy: {} },
  social: { title: "", links: [] },
  quickLinks: [],
  mobileDock: {
    searchLabel: "",
    searchHref: "",
    whatsappLabel: "",
    whatsappPrefill: "",
    phoneLabel: "",
    phoneHref: "",
  },
  innerPages: {
    passagens: {},
    minhasReservas: {},
    notFound: {},
    error: {},
  },
});

export function sanitizeSiteHome(src) {
  const v = src && typeof src === "object" ? src : {};
  const str = (x) => String(x ?? "").trim();

  const ql = (Array.isArray(v.quickLinks) ? v.quickLinks : [])
    .map((x) => ({
      title: str(x?.title),
      href: str(x?.href),
      description: str(x?.description),
    }))
    .filter((x) => x.title && x.href);

  const valueProps = (Array.isArray(v.valueProps) ? v.valueProps : [])
    .map((x) => ({
      icon: str(x?.icon),
      title: str(x?.title),
      body: str(x?.body),
    }))
    .filter((x) => x.title || x.body);

  const promoIn = v.promoBanner && typeof v.promoBanner === "object" ? v.promoBanner : {};
  const seoIn = v.seoBlock && typeof v.seoBlock === "object" ? v.seoBlock : {};
  const payIn = v.paymentCard && typeof v.paymentCard === "object" ? v.paymentCard : {};
  const segIn = v.segment && typeof v.segment === "object" ? v.segment : {};
  const copyIn = segIn.copy && typeof segIn.copy === "object" ? segIn.copy : {};
  const socialIn = v.social && typeof v.social === "object" ? v.social : {};

  const copy = {};
  for (const { key } of SEGMENT_COPY_KEYS) {
    const val = str(copyIn[key]);
    if (val) copy[key] = val;
  }

  const socialLinks = (Array.isArray(socialIn.links) ? socialIn.links : [])
    .map((l) => ({
      label: str(l?.label),
      href: str(l?.href),
    }))
    .filter((l) => l.label && l.href);

  const bullets = (Array.isArray(v.olga?.bullets) ? v.olga.bullets : [])
    .map((b) => str(b))
    .filter(Boolean);

  const searchIn = v.search && typeof v.search === "object" ? v.search : {};
  const metaIn = v.meta && typeof v.meta === "object" ? v.meta : {};
  const dockIn = v.mobileDock && typeof v.mobileDock === "object" ? v.mobileDock : {};

  return {
    format: "site_home.v1",
    meta: {
      title: str(metaIn.title),
      description: str(metaIn.description),
      openGraphDescription: str(metaIn.openGraphDescription),
    },
    search: {
      formTitle: str(searchIn.formTitle),
      formSubtitle: str(searchIn.formSubtitle),
      origemPlaceholder: str(searchIn.origemPlaceholder),
      destinoPlaceholder: str(searchIn.destinoPlaceholder),
      quickLinksTitle: str(searchIn.quickLinksTitle),
      ctaLabel: str(searchIn.ctaLabel),
      ctaSecondaryLabel: str(searchIn.ctaSecondaryLabel),
      oneWayLabel: str(searchIn.oneWayLabel),
      roundTripLabel: str(searchIn.roundTripLabel),
      dateIdaLabel: str(searchIn.dateIdaLabel),
      dateVoltaLabel: str(searchIn.dateVoltaLabel),
      dateIdaFullLabel: str(searchIn.dateIdaFullLabel),
      dateVoltaFullLabel: str(searchIn.dateVoltaFullLabel),
      swapAriaLabel: str(searchIn.swapAriaLabel),
      origemFieldLabel: str(searchIn.origemFieldLabel),
      destinoFieldLabel: str(searchIn.destinoFieldLabel),
      origemListLabel: str(searchIn.origemListLabel),
      destinoListLabel: str(searchIn.destinoListLabel),
      loadingLabel: str(searchIn.loadingLabel),
      chooseOriginFirstLabel: str(searchIn.chooseOriginFirstLabel),
      searchAriaLabel: str(searchIn.searchAriaLabel),
      tripTypeAriaLabel: str(searchIn.tripTypeAriaLabel),
    },
    eyebrow: str(v.eyebrow || v.heroEyebrow),
    heroBadge: str(v.heroBadge ?? v.heroRibbon),
    heroTitle: str(v.heroTitle || v.title),
    heroSubtitle: str(v.heroSubtitle || v.subtitle),
    promoBanner: {
      line: str(promoIn.line),
      code: str(promoIn.code),
      ctaLabel: str(promoIn.ctaLabel),
      ctaHref: str(promoIn.ctaHref),
    },
    valueProps,
    featuredTrips: {
      title: str(v.featuredTrips?.title || v.featuredTitle),
      subtitle: str(v.featuredTrips?.subtitle || v.featuredSubtitle),
      originLabel: str(v.featuredTrips?.originLabel),
      cardCtaLabel: str(v.featuredTrips?.cardCtaLabel),
      fromPriceLabel: str(v.featuredTrips?.fromPriceLabel),
    },
    olga: {
      kicker: str(v.olga?.kicker || v.olga?.eyebrow),
      title: str(v.olga?.title),
      body: str(v.olga?.body || v.olga?.subtitle),
      bullets,
      ctaPrefill: str(v.olga?.ctaPrefill),
      ctaLabel: str(v.olga?.ctaLabel),
      phoneHint: str(v.olga?.phoneHint),
    },
    support: {
      title: str(v.support?.title),
      subtitle: str(v.support?.subtitle),
      sacLabel: str(v.support?.sacLabel),
      sacHref: str(v.support?.sacHref),
      pcdLabel: str(v.support?.pcdLabel),
      pcdHref: str(v.support?.pcdHref),
      formLabel: str(v.support?.formLabel),
      formHref: str(v.support?.formHref),
      olgaPrefill: str(v.support?.olgaPrefill),
      olgaChildren: str(v.support?.olgaChildren),
      accessibilityLabel: str(v.support?.accessibilityLabel),
      accessibilityHref: str(v.support?.accessibilityHref),
    },
    urgentCta: {
      title: str(v.urgentCta?.title),
      subtitle: str(v.urgentCta?.subtitle),
      ctaLabel: str(v.urgentCta?.ctaLabel),
      ctaHref: str(v.urgentCta?.ctaHref),
    },
    fleet: {
      eyebrow: str(v.fleet?.eyebrow),
      title: str(v.fleet?.title),
      body: str(v.fleet?.body || v.fleet?.text),
      ctaLabel: str(v.fleet?.ctaLabel),
      ctaHref: str(v.fleet?.ctaHref),
    },
    seoBlock: {
      title: str(seoIn.title),
      html: str(seoIn.html),
    },
    paymentCard: {
      title: str(payIn.title),
      html: str(payIn.html),
    },
    segment: {
      title: str(segIn.title),
      copy,
    },
    social: {
      title: str(socialIn.title),
      links: socialLinks,
    },
    quickLinks: ql,
    mobileDock: {
      searchLabel: str(dockIn.searchLabel),
      searchHref: str(dockIn.searchHref),
      whatsappLabel: str(dockIn.whatsappLabel),
      whatsappPrefill: str(dockIn.whatsappPrefill),
      phoneLabel: str(dockIn.phoneLabel),
      phoneHref: str(dockIn.phoneHref),
    },
    innerPages: persistInnerPages(v.innerPages),
  };
}

function persistInnerPage(src) {
  if (!src || typeof src !== "object") return undefined;
  const keys = [
    "title",
    "description",
    "eyebrow",
    "homeCrumb",
    "pageCrumb",
    "bodyHtml",
    "faqTitle",
    "elasEyebrow",
    "elasTitle",
    "elasHref",
    "searchHeading",
    "searchLead",
    "actionsTitle",
    "actionsBody",
    "linksTitle",
    "tip",
    "primaryCtaLabel",
    "primaryCtaHref",
    "retryLabel",
    "backLabel",
    "olgaLabel",
  ];
  const out = {};
  for (const key of keys) {
    const val = String(src[key] ?? "").trim();
    if (val) out[key] = val;
  }
  return Object.keys(out).length ? out : undefined;
}

function persistInnerPages(src) {
  if (!src || typeof src !== "object") return undefined;
  const out = {};
  for (const key of ["passagens", "minhasReservas", "notFound", "error"]) {
    const page = persistInnerPage(src[key]);
    if (page) out[key] = page;
  }
  return Object.keys(out).length ? out : undefined;
}

function Section({ id, title, subtitle, badge, defaultExpanded = false, optional = false, children }) {
  return (
    <EditorSection
      id={id}
      title={title}
      subtitle={subtitle}
      badge={badge}
      defaultExpanded={defaultExpanded}
      optional={optional}
    >
      {children}
    </EditorSection>
  );
}

const HOME_NAV = [
  { id: "sec-home-hero", label: "Buscador" },
  { id: "sec-home-search", label: "Rótulos da busca" },
  { id: "sec-home-promo", label: "Promoção" },
  { id: "sec-home-props", label: "Destaques" },
  { id: "sec-home-trips", label: "Destinos" },
  { id: "sec-home-olga", label: "WhatsApp" },
  { id: "sec-home-support", label: "Atendimento" },
  { id: "sec-home-segment", label: "Segmentos" },
  { id: "sec-home-urgent", label: "CTA urgente" },
  { id: "sec-home-fleet", label: "Frota" },
  { id: "sec-home-seo", label: "SEO" },
  { id: "sec-home-payment", label: "Pagamento" },
  { id: "sec-home-social", label: "Redes" },
  { id: "sec-home-quick", label: "Atalhos" },
  { id: "sec-home-dock", label: "Dock mobile" },
  { id: "sec-home-inner", label: "Páginas internas" },
];

export default function SiteHomeEditor({ value, onChange }) {
  const v = value || emptySiteHome();
  const up = (patch) => onChange({ ...v, ...patch });
  const upNested = (key, patch) => up({ [key]: { ...(v[key] || {}), ...patch } });

  const setQuickLink = (i, patch) => {
    const a = [...(v.quickLinks || [])];
    a[i] = { ...a[i], ...patch };
    up({ quickLinks: a });
  };

  const setValueProp = (i, patch) => {
    const a = [...(v.valueProps || [])];
    a[i] = { ...a[i], ...patch };
    up({ valueProps: a });
  };

  const setSocialLink = (i, patch) => {
    const a = [...(v.social?.links || [])];
    a[i] = { ...a[i], ...patch };
    upNested("social", { links: a });
  };

  const setSegmentCopy = (key, val) => {
    upNested("segment", { copy: { ...(v.segment?.copy || {}), [key]: val } });
  };

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Página inicial do site. Abra uma seção por vez — a maioria dos visitantes vê primeiro o buscador e os destaques.
      </Typography>

      <EditorFormNav sections={HOME_NAV} />

      <Section id="sec-home-hero" title="Buscador de passagens" subtitle="Título e textos acima da busca de viagens" defaultExpanded>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              label="Eyebrow (faixa superior)"
              value={v.eyebrow || ""}
              onChange={(e) => up({ eyebrow: e.target.value })}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Título do buscador"
              value={v.heroTitle || ""}
              onChange={(e) => up({ heroTitle: e.target.value })}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Selo (ex.: parcelamento)"
              value={v.heroBadge || ""}
              onChange={(e) => up({ heroBadge: e.target.value })}
              fullWidth
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Subtítulo"
              value={v.heroSubtitle || ""}
              onChange={(e) => up({ heroSubtitle: e.target.value })}
              fullWidth
              multiline
              minRows={2}
            />
          </Grid>
        </Grid>
      </Section>

      <Section id="sec-home-search" title="Rótulos da busca" subtitle="Textos dos campos e botões do buscador em todas as páginas">
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField label="Título do formulário" value={v.search?.formTitle || ""} onChange={(e) => upNested("search", { formTitle: e.target.value })} fullWidth />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField label="Título dos atalhos" value={v.search?.quickLinksTitle || ""} onChange={(e) => upNested("search", { quickLinksTitle: e.target.value })} fullWidth />
          </Grid>
          <Grid item xs={12}>
            <TextField label="Subtítulo do formulário" value={v.search?.formSubtitle || ""} onChange={(e) => upNested("search", { formSubtitle: e.target.value })} fullWidth multiline minRows={2} />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField label="Placeholder origem" value={v.search?.origemPlaceholder || ""} onChange={(e) => upNested("search", { origemPlaceholder: e.target.value })} fullWidth />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField label="Placeholder destino" value={v.search?.destinoPlaceholder || ""} onChange={(e) => upNested("search", { destinoPlaceholder: e.target.value })} fullWidth />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField label="Somente ida" value={v.search?.oneWayLabel || ""} onChange={(e) => upNested("search", { oneWayLabel: e.target.value })} fullWidth />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField label="Ida e volta" value={v.search?.roundTripLabel || ""} onChange={(e) => upNested("search", { roundTripLabel: e.target.value })} fullWidth />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField label="Botão buscar" value={v.search?.ctaLabel || ""} onChange={(e) => upNested("search", { ctaLabel: e.target.value })} fullWidth />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField label="Botão buscar (páginas internas)" value={v.search?.ctaSecondaryLabel || ""} onChange={(e) => upNested("search", { ctaSecondaryLabel: e.target.value })} fullWidth />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField label="Rótulo data ida" value={v.search?.dateIdaLabel || ""} onChange={(e) => upNested("search", { dateIdaLabel: e.target.value })} fullWidth />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField label="Rótulo data volta" value={v.search?.dateVoltaLabel || ""} onChange={(e) => upNested("search", { dateVoltaLabel: e.target.value })} fullWidth />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField label="Rótulo campo origem" value={v.search?.origemFieldLabel || ""} onChange={(e) => upNested("search", { origemFieldLabel: e.target.value })} fullWidth />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField label="Rótulo campo destino" value={v.search?.destinoFieldLabel || ""} onChange={(e) => upNested("search", { destinoFieldLabel: e.target.value })} fullWidth />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField label="Lista origem" value={v.search?.origemListLabel || ""} onChange={(e) => upNested("search", { origemListLabel: e.target.value })} fullWidth />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField label="Lista destino" value={v.search?.destinoListLabel || ""} onChange={(e) => upNested("search", { destinoListLabel: e.target.value })} fullWidth />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField label="Carregando" value={v.search?.loadingLabel || ""} onChange={(e) => upNested("search", { loadingLabel: e.target.value })} fullWidth />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField label="Escolha a origem primeiro" value={v.search?.chooseOriginFirstLabel || ""} onChange={(e) => upNested("search", { chooseOriginFirstLabel: e.target.value })} fullWidth />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField label="Aria da busca" value={v.search?.searchAriaLabel || ""} onChange={(e) => upNested("search", { searchAriaLabel: e.target.value })} fullWidth />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField label="Aria tipo de viagem" value={v.search?.tripTypeAriaLabel || ""} onChange={(e) => upNested("search", { tripTypeAriaLabel: e.target.value })} fullWidth />
          </Grid>
        </Grid>
      </Section>

      <Section id="sec-home-promo" title="Faixa promocional" subtitle="Banner de oferta ou cupom abaixo do buscador">
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              label="Texto da faixa"
              value={v.promoBanner?.line || ""}
              onChange={(e) => upNested("promoBanner", { line: e.target.value })}
              fullWidth
              multiline
              minRows={2}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              label="Código promocional (opcional)"
              value={v.promoBanner?.code || ""}
              onChange={(e) => upNested("promoBanner", { code: e.target.value })}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              label="Botão — texto"
              value={v.promoBanner?.ctaLabel || ""}
              onChange={(e) => upNested("promoBanner", { ctaLabel: e.target.value })}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              label="Botão — link"
              value={v.promoBanner?.ctaHref || ""}
              onChange={(e) => upNested("promoBanner", { ctaHref: e.target.value })}
              fullWidth
            />
          </Grid>
        </Grid>
      </Section>

      <Section id="sec-home-props" title="Propostas de valor" subtitle="Ícones com benefícios rápidos (parcelamento, acessibilidade…)" badge={(v.valueProps || []).length || null}>
        <EditorListHeader
          onAdd={() => up({ valueProps: [...(v.valueProps || []), emptyValueProp()] })}
          addLabel="+ Adicionar proposta"
        />
        {(v.valueProps || []).map((vp, i) => (
          <EditorListItem
            key={i}
            index={i}
            title={vp.title ? `Item ${i + 1}: ${vp.title}` : `Item ${i + 1}`}
            summary={vp.body}
            defaultExpanded={i === (v.valueProps || []).length - 1}
            onDelete={() => up({ valueProps: (v.valueProps || []).filter((_, idx) => idx !== i) })}
          >
            <Box sx={{ mb: 1.5 }}>
              <EmojiIconPicker value={vp.icon || ""} onChange={(icon) => setValueProp(i, { icon })} />
            </Box>
            <Grid container spacing={1.5}>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Título"
                  value={vp.title || ""}
                  onChange={(e) => setValueProp(i, { title: e.target.value })}
                  size="small"
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Texto"
                  value={vp.body || ""}
                  onChange={(e) => setValueProp(i, { body: e.target.value })}
                  size="small"
                  fullWidth
                />
              </Grid>
            </Grid>
          </EditorListItem>
        ))}
      </Section>

      <Section id="sec-home-trips" title="Destinos em destaque" subtitle="Título da seção — os cards vêm dos produtos em destaque">
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              label="Título"
              value={v.featuredTrips?.title || ""}
              onChange={(e) => upNested("featuredTrips", { title: e.target.value })}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Subtítulo"
              value={v.featuredTrips?.subtitle || ""}
              onChange={(e) => upNested("featuredTrips", { subtitle: e.target.value })}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              label="Rótulo do destino no card"
              value={v.featuredTrips?.originLabel || ""}
              onChange={(e) => upNested("featuredTrips", { originLabel: e.target.value })}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              label="Botão do card"
              value={v.featuredTrips?.cardCtaLabel || ""}
              onChange={(e) => upNested("featuredTrips", { cardCtaLabel: e.target.value })}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              label="Prefixo do preço"
              value={v.featuredTrips?.fromPriceLabel || ""}
              onChange={(e) => upNested("featuredTrips", { fromPriceLabel: e.target.value })}
              fullWidth
            />
          </Grid>
        </Grid>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
          Os cards de trecho vêm de Produtos com Destaque ativo — não deste formulário.
        </Typography>
      </Section>

      <Section id="sec-home-olga" title="WhatsApp / assistente Olga" subtitle="Faixa de atendimento por mensagem">
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              label="Kicker / etiqueta"
              value={v.olga?.kicker || ""}
              onChange={(e) => upNested("olga", { kicker: e.target.value })}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Título da faixa"
              value={v.olga?.title || ""}
              onChange={(e) => upNested("olga", { title: e.target.value })}
              fullWidth
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Texto / corpo"
              value={v.olga?.body || ""}
              onChange={(e) => upNested("olga", { body: e.target.value })}
              fullWidth
              multiline
              minRows={2}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Rótulo do botão"
              value={v.olga?.ctaLabel || ""}
              onChange={(e) => upNested("olga", { ctaLabel: e.target.value })}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Dica de telefone"
              value={v.olga?.phoneHint || ""}
              onChange={(e) => upNested("olga", { phoneHint: e.target.value })}
              fullWidth
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Mensagem inicial (prefill WhatsApp)"
              value={v.olga?.ctaPrefill || ""}
              onChange={(e) => upNested("olga", { ctaPrefill: e.target.value })}
              fullWidth
            />
          </Grid>
        </Grid>
        <StringListEditor
          label="Destaques (bullets)"
          items={v.olga?.bullets}
          onChange={(bullets) => upNested("olga", { bullets })}
        />
      </Section>

      <Section id="sec-home-support" title="Central de atendimento" subtitle="SAC, PcD, formulário e links de contato">
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              label="Título da seção"
              value={v.support?.title || ""}
              onChange={(e) => upNested("support", { title: e.target.value })}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Subtítulo"
              value={v.support?.subtitle || ""}
              onChange={(e) => upNested("support", { subtitle: e.target.value })}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Rótulo SAC"
              value={v.support?.sacLabel || ""}
              onChange={(e) => upNested("support", { sacLabel: e.target.value })}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Link SAC (tel: ou URL)"
              value={v.support?.sacHref || ""}
              onChange={(e) => upNested("support", { sacHref: e.target.value })}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Rótulo PcD"
              value={v.support?.pcdLabel || ""}
              onChange={(e) => upNested("support", { pcdLabel: e.target.value })}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Link PcD"
              value={v.support?.pcdHref || ""}
              onChange={(e) => upNested("support", { pcdHref: e.target.value })}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Rótulo acessibilidade"
              value={v.support?.accessibilityLabel || ""}
              onChange={(e) => upNested("support", { accessibilityLabel: e.target.value })}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Link acessibilidade"
              value={v.support?.accessibilityHref || ""}
              onChange={(e) => upNested("support", { accessibilityHref: e.target.value })}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Rótulo formulário"
              value={v.support?.formLabel || ""}
              onChange={(e) => upNested("support", { formLabel: e.target.value })}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Link formulário"
              value={v.support?.formHref || ""}
              onChange={(e) => upNested("support", { formHref: e.target.value })}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Prefill Olga (central)"
              value={v.support?.olgaPrefill || ""}
              onChange={(e) => upNested("support", { olgaPrefill: e.target.value })}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Rótulo filho WhatsApp"
              value={v.support?.olgaChildren || ""}
              onChange={(e) => upNested("support", { olgaChildren: e.target.value })}
              fullWidth
            />
          </Grid>
        </Grid>
      </Section>

      <Section id="sec-home-segment" title="Textos por perfil de passageiro" subtitle="Mensagens diferentes para idoso, estudante, PcD…" optional>
        <TextField
          label="Título da seção"
          value={v.segment?.title || ""}
          onChange={(e) => upNested("segment", { title: e.target.value })}
          fullWidth
          sx={{ mb: 2 }}
        />
        {SEGMENT_COPY_KEYS.map(({ key, label }) => (
          <TextField
            key={key}
            fullWidth
            multiline
            minRows={2}
            label={`Texto — ${label}`}
            value={v.segment?.copy?.[key] || ""}
            onChange={(e) => setSegmentCopy(key, e.target.value)}
            sx={{ mb: 1.5 }}
          />
        ))}
      </Section>

      <Section id="sec-home-urgent" title="Chamada urgente" subtitle="Bloco de destaque para ação imediata">
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              label="Título"
              value={v.urgentCta?.title || ""}
              onChange={(e) => upNested("urgentCta", { title: e.target.value })}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Subtítulo"
              value={v.urgentCta?.subtitle || ""}
              onChange={(e) => upNested("urgentCta", { subtitle: e.target.value })}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Botão — texto"
              value={v.urgentCta?.ctaLabel || ""}
              onChange={(e) => upNested("urgentCta", { ctaLabel: e.target.value })}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Botão — link"
              value={v.urgentCta?.ctaHref || ""}
              onChange={(e) => upNested("urgentCta", { ctaHref: e.target.value })}
              fullWidth
            />
          </Grid>
        </Grid>
      </Section>

      <Section id="sec-home-fleet" title="Frota e conforto" subtitle="Seção sobre ônibus e experiência de viagem">
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              label="Eyebrow"
              value={v.fleet?.eyebrow || ""}
              onChange={(e) => upNested("fleet", { eyebrow: e.target.value })}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Título"
              value={v.fleet?.title || ""}
              onChange={(e) => upNested("fleet", { title: e.target.value })}
              fullWidth
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Corpo (HTML permitido)"
              value={v.fleet?.body || ""}
              onChange={(e) => upNested("fleet", { body: e.target.value })}
              fullWidth
              multiline
              minRows={3}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Botão — texto"
              value={v.fleet?.ctaLabel || ""}
              onChange={(e) => upNested("fleet", { ctaLabel: e.target.value })}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Botão — link"
              value={v.fleet?.ctaHref || ""}
              onChange={(e) => upNested("fleet", { ctaHref: e.target.value })}
              fullWidth
            />
          </Grid>
        </Grid>
      </Section>

      <Section id="sec-home-seo" title="Texto SEO (rodapé)" subtitle="Parágrafo extra para buscadores — pode usar HTML simples" optional>
        <TextField
          label="Título"
          value={v.seoBlock?.title || ""}
          onChange={(e) => upNested("seoBlock", { title: e.target.value })}
          fullWidth
          sx={{ mb: 2 }}
        />
        <TextField
          label="HTML"
          value={v.seoBlock?.html || ""}
          onChange={(e) => upNested("seoBlock", { html: e.target.value })}
          fullWidth
          multiline
          minRows={4}
        />
      </Section>

      <Section id="sec-home-payment" title="Card de pagamento" subtitle="Resumo das formas de pagamento" optional>
        <TextField
          label="Título"
          value={v.paymentCard?.title || ""}
          onChange={(e) => upNested("paymentCard", { title: e.target.value })}
          fullWidth
          sx={{ mb: 2 }}
        />
        <TextField
          label="HTML"
          value={v.paymentCard?.html || ""}
          onChange={(e) => upNested("paymentCard", { html: e.target.value })}
          fullWidth
          multiline
          minRows={3}
        />
      </Section>

      <Section id="sec-home-social" title="Redes sociais" subtitle="Título e links para Instagram, Facebook…" badge={(v.social?.links || []).length || null}>
        <TextField
          label="Título da seção"
          value={v.social?.title || ""}
          onChange={(e) => upNested("social", { title: e.target.value })}
          fullWidth
          sx={{ mb: 2 }}
        />
        {(v.social?.links || []).map((link, i) => (
          <Box key={i} sx={{ display: "grid", gridTemplateColumns: "1fr 1fr auto", gap: 1, mb: 1 }}>
            <TextField
              label="Rede"
              value={link.label || ""}
              onChange={(e) => setSocialLink(i, { label: e.target.value })}
              size="small"
            />
            <TextField
              label="URL"
              value={link.href || ""}
              onChange={(e) => setSocialLink(i, { href: e.target.value })}
              size="small"
            />
            <IconButton onClick={() => upNested("social", { links: (v.social?.links || []).filter((_, idx) => idx !== i) })}>
              <DeleteOutlineRoundedIcon />
            </IconButton>
          </Box>
        ))}
        <Button
          startIcon={<AddRoundedIcon />}
          onClick={() => upNested("social", { links: [...(v.social?.links || []), emptySocialLink()] })}
        >
          Adicionar rede
        </Button>
      </Section>

      <Section id="sec-home-quick" title="Atalhos rápidos" subtitle="Links úteis no final da home" badge={(v.quickLinks || []).length || null}>
        {(v.quickLinks || []).map((link, i) => (
          <Box key={i} sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr auto", gap: 1, mb: 1 }}>
            <TextField
              label="Título"
              value={link.title || ""}
              onChange={(e) => setQuickLink(i, { title: e.target.value })}
              size="small"
            />
            <TextField
              label="URL"
              value={link.href || ""}
              onChange={(e) => setQuickLink(i, { href: e.target.value })}
              size="small"
            />
            <TextField
              label="Descrição (opcional)"
              value={link.description || ""}
              onChange={(e) => setQuickLink(i, { description: e.target.value })}
              size="small"
            />
            <IconButton onClick={() => up({ quickLinks: (v.quickLinks || []).filter((_, idx) => idx !== i) })}>
              <DeleteOutlineRoundedIcon />
            </IconButton>
          </Box>
        ))}
        <Button
          startIcon={<AddRoundedIcon />}
          onClick={() => up({ quickLinks: [...(v.quickLinks || []), emptyQuickLink()] })}
        >
          Adicionar atalho
        </Button>
      </Section>

      <Section id="sec-home-dock" title="Dock mobile" subtitle="Barra fixa no rodapé do celular">
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField label="Botão buscar" value={v.mobileDock?.searchLabel || ""} onChange={(e) => upNested("mobileDock", { searchLabel: e.target.value })} fullWidth />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField label="Link buscar" value={v.mobileDock?.searchHref || ""} onChange={(e) => upNested("mobileDock", { searchHref: e.target.value })} fullWidth />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField label="Botão WhatsApp" value={v.mobileDock?.whatsappLabel || ""} onChange={(e) => upNested("mobileDock", { whatsappLabel: e.target.value })} fullWidth />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField label="Mensagem inicial WhatsApp" value={v.mobileDock?.whatsappPrefill || ""} onChange={(e) => upNested("mobileDock", { whatsappPrefill: e.target.value })} fullWidth />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField label="Botão telefone" value={v.mobileDock?.phoneLabel || ""} onChange={(e) => upNested("mobileDock", { phoneLabel: e.target.value })} fullWidth />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField label="Link telefone" value={v.mobileDock?.phoneHref || ""} onChange={(e) => upNested("mobileDock", { phoneHref: e.target.value })} fullWidth />
          </Grid>
        </Grid>
      </Section>

      <Section id="sec-home-inner" title="Páginas internas" subtitle="Textos de passagens, minhas viagens, 404 e erro — sem fallback no site" optional>
        {[
          ["passagens", "Passagens"],
          ["minhasReservas", "Minhas viagens"],
          ["notFound", "Página não encontrada"],
          ["error", "Erro"],
        ].map(([key, label]) => (
          <Box key={key} sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1.5 }}>{label}</Typography>
            <Grid container spacing={2}>
              {[
                ["title", "Título"],
                ["description", "Descrição (SEO)"],
                ["eyebrow", "Eyebrow"],
                ["homeCrumb", "Crumb início"],
                ["pageCrumb", "Crumb da página"],
                ["faqTitle", "Título do FAQ"],
                ["elasEyebrow", "ELAS eyebrow"],
                ["elasTitle", "ELAS título"],
                ["elasHref", "ELAS link"],
                ["searchHeading", "Título da busca"],
                ["searchLead", "Texto da busca"],
                ["actionsTitle", "Título das ações"],
                ["actionsBody", "Texto das ações"],
                ["linksTitle", "Título dos atalhos"],
                ["tip", "Dica"],
                ["primaryCtaLabel", "CTA principal"],
                ["primaryCtaHref", "CTA link"],
                ["retryLabel", "Tentar novamente"],
                ["backLabel", "Voltar"],
                ["olgaLabel", "Olga / WhatsApp"],
              ].map(([field, fieldLabel]) => (
                <Grid item xs={12} md={field === "description" || field === "actionsBody" || field === "searchLead" ? 12 : 6} key={field}>
                  <TextField
                    label={fieldLabel}
                    value={v.innerPages?.[key]?.[field] || ""}
                    onChange={(e) =>
                      up({
                        innerPages: {
                          ...(v.innerPages || {}),
                          [key]: { ...(v.innerPages?.[key] || {}), [field]: e.target.value },
                        },
                      })
                    }
                    fullWidth
                    multiline={field === "description" || field === "actionsBody" || field === "searchLead" || field === "bodyHtml"}
                    minRows={field === "description" || field === "bodyHtml" ? 2 : 1}
                  />
                </Grid>
              ))}
              <Grid item xs={12}>
                <TextField
                  label="HTML do corpo"
                  value={v.innerPages?.[key]?.bodyHtml || ""}
                  onChange={(e) =>
                    up({
                      innerPages: {
                        ...(v.innerPages || {}),
                        [key]: { ...(v.innerPages?.[key] || {}), bodyHtml: e.target.value },
                      },
                    })
                  }
                  fullWidth
                  multiline
                  minRows={3}
                />
              </Grid>
            </Grid>
          </Box>
        ))}
      </Section>

      <Divider sx={{ my: 1 }} />
    </Box>
  );
}
